import jQuery from 'jquery';
import axios from 'axios';
import { installReconsentInterceptor } from './support/reconsentInterceptor';

// Expose jQuery before Bootstrap is loaded so that its jQuery bridges register correctly.
window.$ = window.$ || jQuery;
window.jQuery = window.jQuery || jQuery;
window.bootstrap = window.bootstrap || {};

// Ensure a global Vue compatibility object is available for legacy helpers.
window.Vue = window.Vue || {};

const queuedPluginInvocations = new Map();
const bootstrapPluginNames = [
    'alert',
    'button',
    'carousel',
    'collapse',
    'dropdown',
    'modal',
    'offcanvas',
    'popover',
    'scrollspy',
    'tab',
    'toast',
    'tooltip',
];

const createQueuedPlugin = (name) => function (...args) {
    const queue = queuedPluginInvocations.get(name) || [];
    this.each(function () {
        queue.push({ element: this, args });
    });
    queuedPluginInvocations.set(name, queue);
    return this;
};

bootstrapPluginNames.forEach((pluginName) => {
    if (!jQuery.fn[pluginName]) {
        jQuery.fn[pluginName] = createQueuedPlugin(pluginName);
    }
});

// Axios defaults shared across the app.
window.axios = axios;
window.axios.defaults.headers.common = {
    'X-CSRF-TOKEN': window.Laravel?.csrfToken,
    'X-Requested-With': 'XMLHttpRequest',
};

// B5c: global re-consent response handling — see support/reconsentInterceptor.js.
installReconsentInterceptor(window.axios);

/**
 * Upgrade legacy Bootstrap 3 data-* attributes to the Bootstrap 5 data-bs-* equivalents.
 * This allows us to keep markup changes to a minimum while adopting the new runtime.
 */
const legacyBootstrapAttributes = {
    toggle: 'bs-toggle',
    target: 'bs-target',
    dismiss: 'bs-dismiss',
    spy: 'bs-spy',
    parent: 'bs-parent',
    offset: 'bs-offset',
    container: 'bs-container',
    trigger: 'bs-trigger',
    placement: 'bs-placement',
    boundary: 'bs-boundary',
    title: 'bs-title',
    html: 'bs-html',
    animation: 'bs-animation',
    delay: 'bs-delay',
    ride: 'bs-ride',
    slide: 'bs-slide',
    template: 'bs-template',
    content: 'bs-content',
    'toggle-class': 'bs-toggle-class',
    'original-title': 'bs-original-title',
    'fallback-placement': 'bs-fallback-placement',
};

const legacyAttributeSelector = Object.keys(legacyBootstrapAttributes)
    .map((attribute) => `[data-${attribute}]`)
    .join(', ');

const applyBootstrapAttributePatch = (element) => {
    if (!(element instanceof Element)) {
        return;
    }

    Object.entries(legacyBootstrapAttributes).forEach(([legacy, modern]) => {
        const legacyAttribute = `data-${legacy}`;
        const modernAttribute = `data-${modern}`;

        const legacyValue = element.getAttribute(legacyAttribute);
        const modernValue = element.getAttribute(modernAttribute);

        if (legacyValue !== null && modernValue === null) {
            element.setAttribute(modernAttribute, legacyValue);
        } else if (modernValue !== null && legacyValue === null) {
            element.setAttribute(legacyAttribute, modernValue);
        }
    });
};

const upgradeBootstrapAttributes = (root) => {
    if (!root) {
        return;
    }

    const nodes = [];

    if (root instanceof Element) {
        if (root.matches?.(legacyAttributeSelector)) {
            nodes.push(root);
        }
        nodes.push(...Array.from(root.querySelectorAll?.(legacyAttributeSelector) || []));
    } else if (root instanceof Document || root instanceof DocumentFragment) {
        nodes.push(...Array.from(root.querySelectorAll?.(legacyAttributeSelector) || []));
    }

    nodes.forEach(applyBootstrapAttributePatch);
};

if (typeof document !== 'undefined') {
    upgradeBootstrapAttributes(document);

    if (typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node instanceof Element || node instanceof DocumentFragment) {
                            upgradeBootstrapAttributes(node);
                        }
                    });
                } else if (mutation.type === 'attributes' && mutation.target instanceof Element) {
                    applyBootstrapAttributePatch(mutation.target);
                }
            });
        });

        const observedAttributes = Object.entries(legacyBootstrapAttributes).flatMap(([legacy, modern]) => [
            `data-${legacy}`,
            `data-${modern}`,
        ]);

        observer.observe(document.documentElement, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: observedAttributes,
        });

        window.__bootstrapDataAttributeObserver = observer;
    }

    window.applyBootstrapAttributePolyfill = upgradeBootstrapAttributes;
}

/**
 * Load Bootstrap asynchronously so the module sees jQuery on the global scope.
 * Consumers can await `window.bootstrapReady` when they need to interact with the runtime directly.
 */
const bootstrapPromise = import('bootstrap')
    .then((bootstrapModule) => {
        window.bootstrap = bootstrapModule;

        queuedPluginInvocations.forEach((entries, name) => {
            const plugin = jQuery.fn[name];
            if (typeof plugin !== 'function') {
                return;
            }

            entries.forEach(({ element, args }) => {
                plugin.apply(jQuery(element), args);
            });
        });
        queuedPluginInvocations.clear();

        return bootstrapModule;
    })
    .catch((error) => {
        console.error('Unable to load Bootstrap runtime', error);
        return null;
    });

window.bootstrapReady = bootstrapPromise;

/**
 * Echo exposes an expressive API for subscribing to channels and listening
 * for events that are broadcast by Laravel. Echo and event broadcasting
 * allows your team to easily build robust real-time web applications.
 *
 * import Echo from "laravel-echo"
 * window.Echo = new Echo({
 *     broadcaster: 'pusher',
 *     key: 'your-pusher-key'
 * });
 */
